import { ReactNode } from "react";

import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { StepFlowStepper } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowStepper";

interface BaseProps {
  dataTestid?: string;
  title: string;
  description?: string;
  headerAction?: ReactNode;
  children: ReactNode;
}

interface WithStepperProps extends BaseProps {
  step: number;
  totalSteps: number;
  stepLabel: string;
  menuItems: StepMenuItem[];
  menuToggleLabel: string;
}

/** A single-step flow (e.g. Sammelverschiebung): title and description only. */
type WithoutStepperProps = BaseProps;

type StepFlowLayoutProps = WithStepperProps | WithoutStepperProps;

const hasStepper = (props: StepFlowLayoutProps): props is WithStepperProps =>
  "step" in props;

export const StepFlowLayout = (props: StepFlowLayoutProps) => {
  const { dataTestid, title, description, headerAction, children } = props;

  return (
    <section data-testid={dataTestid} className="w-full">
      <CardContainer
        className="flex w-full flex-col gap-6 rounded-lg p-6"
        variant="ivory"
      >
        {headerAction}

        <div className="flex w-full flex-col gap-4">
          <h1 className="text-headline-xl font-semibold leading-xs text-foreground-primary">
            {title}
          </h1>
          {hasStepper(props) ? (
            <StepFlowStepper
              step={props.step}
              totalSteps={props.totalSteps}
              stepLabel={props.stepLabel}
              description={description}
              menuItems={props.menuItems}
              menuToggleLabel={props.menuToggleLabel}
            />
          ) : (
            description && (
              <p className="text-body-sm leading-none text-foreground-primary">
                {description}
              </p>
            )
          )}
        </div>

        {children}
      </CardContainer>
    </section>
  );
};
